import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AppStateService } from './app-state.service';

// Dialogs a PAMP page can open straight away when given `?action=<id>`:
// - resendInvite: patient dashboard (/patients/dashboard/:id)
// - editRecurrence: questionnaire templates list (/template/todo-list)
export type PampDashboardAction = 'resendInvite' | 'editRecurrence';
const PAMP_DASHBOARD_ACTIONS: PampDashboardAction[] = ['resendInvite', 'editRecurrence'];

export interface PampActionResult {
  action: PampDashboardAction;
  success: boolean;
}

// The message payload is untrusted input; only a well-formed result for a known action is published.
function toPampActionResult(result: unknown): PampActionResult | null {
  if (!result || typeof result !== 'object') {
    return null;
  }
  const { action, success } = result as { action?: unknown; success?: unknown };
  const isKnownAction = PAMP_DASHBOARD_ACTIONS.indexOf(action as PampDashboardAction) !== -1;
  return isKnownAction && typeof success === 'boolean'
    ? { action: action as PampDashboardAction, success }
    : null;
}

@Injectable({
  providedIn: 'root'
})
export class PampIframeService {
  private static readonly QUESTIONNAIRE_TEMPLATES_ROUTE = '/template/todo-list';

  private showIframeSubject = new BehaviorSubject<boolean>(false);
  public showIframe$: Observable<boolean> = this.showIframeSubject.asObservable();

  private hasLoadedIframeSubject = new BehaviorSubject<boolean>(false);
  public hasLoadedIframe$: Observable<boolean> = this.hasLoadedIframeSubject.asObservable();

  private iframeUrlSubject = new BehaviorSubject<SafeResourceUrl | null>(null);
  public iframeUrl$: Observable<SafeResourceUrl | null> = this.iframeUrlSubject.asObservable();

  private isPatientAppModeSubject = new BehaviorSubject<boolean>(false);
  public isPatientAppMode$: Observable<boolean> = this.isPatientAppModeSubject.asObservable();

  private iframeClosedSubject = new Subject<void>();
  public iframeClosed$: Observable<void> = this.iframeClosedSubject.asObservable();

  // Outcome of a flow we launched with `?action=` (see openPatientDashboard). PAMP reports it on
  // the closeIframe message so the confirmation can be shown here, after the overlay is gone.
  private actionResultSubject = new Subject<PampActionResult>();
  public actionResult$: Observable<PampActionResult> = this.actionResultSubject.asObservable();

  private iframeElement: HTMLIFrameElement | null = null;
  private hasPushedHistoryState = false;
  private readonly MAXIMUM_HISTORY_UNWIND_STEPS = 10;

  private pendingNavData: {
    route: string;
    queryParams?: Record<string, any>;
    hideNav: boolean;
  } | null = null;

  constructor(private sanitizer: DomSanitizer, private appStateService: AppStateService) {
    window.addEventListener('popstate', (event: PopStateEvent) => {
      this.handlePopState(event);
    });
  }

  private handlePopState(event: PopStateEvent): void {
    if (this.showIframeSubject.value && (!event.state || !event.state.pampIframeOpen)) {
      this.closeIframe(true);
    }
  }

  public registerIframeElement(iframe: HTMLIFrameElement): void {
    this.iframeElement = iframe;
  }

  public getPampUrl(): string {
    const currentHostname = window.location.hostname;
    const protocol = window.location.protocol;
    const defaultPAMPUrl = environment.PAMPUrl;

    if (currentHostname.includes('csidryeye.com')) {
      if (
        currentHostname === 'dep-pamp.dev.csidryeye.com' ||
        currentHostname === 'dep-pamp.csidryeye.com'
      ) {
        return `${protocol}//pamp.dev.csidryeye.com/`;
      } else {
        return `${protocol}//pamp.${currentHostname}/`;
      }
    }

    return defaultPAMPUrl;
  }

  // Absolute PAMP URL for a route, used when the clinic has opted out of iframe embedding.
  private getPampAbsoluteUrl(route: string, queryParams?: Record<string, string>): string {
    const url = this.getPampUrl().replace(/\/$/, '') + route;
    const query = Object.keys(queryParams || {})
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');
    return query ? `${url}?${query}` : url;
  }

  public postMessageToIframe(data: any): void {
    if (this.iframeElement && this.iframeElement.contentWindow) {
      try {
        const origin = new URL(this.getPampUrl()).origin;
        this.iframeElement.contentWindow.postMessage(data, origin);
      } catch (e) {
        console.error('Error sending postMessage to PAMP iframe', e);
      }
    }
  }

  public navigateToPamp(navData: {
    route: string;
    queryParams?: Record<string, any>;
    hideNav: boolean;
  }): void {
    if (!this.appStateService.getOpenInIframeValue()) {
      return;
    }

    if (!this.showIframeSubject.value && !this.hasPushedHistoryState) {
      history.pushState({ pampIframeOpen: true }, '', window.location.href);
      this.hasPushedHistoryState = true;
    }

    if (this.hasLoadedIframeSubject.value) {
      // First navigate the iframe route via postMessage
      this.postMessageToIframe({
        type: 'navigatePamp',
        route: navData.route,
        queryParams: navData.queryParams,
        hideNav: navData.hideNav
      });
      this.postMessageToIframe({ type: 'iframeOpened' });

      // Then open/show the iframe overlay visually
      setTimeout(() => {
        this.isPatientAppModeSubject.next(navData.hideNav);
        this.showIframeSubject.next(true);
      }, 400);
    } else {
      // First open: pass ONLY base PAMP URL to iframe src and store navData until 'pampReady' handshake
      this.pendingNavData = navData;
      const baseUrl = this.getPampUrl();

      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(baseUrl);
      this.iframeUrlSubject.next(safeUrl);
      this.hasLoadedIframeSubject.next(true);
      this.isPatientAppModeSubject.next(navData.hideNav);
      this.showIframeSubject.next(true);
    }
  }

  public openPatientDashboard(
    patientId: string,
    queryParams?: Record<string, any>
  ): void {
    this.navigateToPamp({
      route: `/patients/dashboard/${patientId}`,
      queryParams,
      hideNav: true
    });
  }

  // Opens the dashboard with one of its dialogs already showing. PAMP closes the overlay when the
  // dialog is dismissed and reports the outcome through actionResult$.
  public openPatientDashboardAction(patientId: string, action: PampDashboardAction): void {
    this.openPatientDashboard(patientId, { action });
  }

  public openPatientEnrollment(patientId: string, isDefault: boolean): void {
    const queryParams: Record<string, any> = {};
    if (isDefault) {
      queryParams['quickEnroll'] = 'true';
    }
    this.navigateToPamp({
      route: `/patients/enrollment/${patientId}`,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      hideNav: true
    });
  }

  public openPatientReEnrollment(patientId: string, isDefault: boolean): void {
    const queryParams: Record<string, any> = {};
    if (isDefault) {
      queryParams['quickEnroll'] = 'true';
    }
    this.navigateToPamp({
      route: `/patients/re-enrollment/${patientId}`,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      hideNav: true
    });
  }

  public openPampApp(): void {
    this.navigateToPamp({
      route: '/',
      hideNav: false
    });
  }

  // Opens PAMP's dialog for choosing which questionnaires recur. PAMP reads `?action=` once on
  // the templates list and closes the overlay when the dialog is dismissed (see actionResult$).
  // Clinics that opted out of embedding get the same page in a new tab instead.
  public openQuestionnaireRecurrenceEditor(): void {
    const route = PampIframeService.QUESTIONNAIRE_TEMPLATES_ROUTE;
    const queryParams: Record<string, string> = { action: 'editRecurrence' };
    if (!this.appStateService.getOpenInIframeValue()) {
      window.open(this.getPampAbsoluteUrl(route, queryParams), '_blank', 'noopener');
      return;
    }
    this.navigateToPamp({ route, queryParams, hideNav: true });
  }

  public openProductRefillRequests(queryParams?: Record<string, any>): void {
    this.navigateToPamp({
      route: '/product-refill-requests',
      queryParams,
      hideNav: true
    });
  }

  public handleIncomingMessage(event: MessageEvent, router: Router): void {
    const pampOrigin = new URL(this.getPampUrl()).origin;
    if (
      event.origin !== pampOrigin &&
      !event.origin.includes('csidryeye.com') &&
      !event.origin.includes('localhost')
    ) {
      return;
    }

    if (!event.data || !event.data.type) {
      return;
    }

    const { type, url, route, path, fromPopState } = event.data;

    switch (type) {
      case 'pampReady':
        if (this.pendingNavData) {
          const navData = this.pendingNavData;
          this.pendingNavData = null;

          this.postMessageToIframe({
            type: 'navigatePamp',
            route: navData.route,
            queryParams: navData.queryParams,
            hideNav: navData.hideNav
          });
          this.postMessageToIframe({ type: 'iframeOpened' });
        }
        break;

      case 'closeIframe': {
        this.closeIframe(!!fromPopState, event.data.depth);
        const actionResult = toPampActionResult(event.data.result);
        if (actionResult) {
          this.actionResultSubject.next(actionResult);
        }
        break;
      }

      case 'navigate': {
        const targetUrl = url || route || path;
        this.closeIframe(!!fromPopState, event.data && event.data.depth);
        if (targetUrl) {
          let navigatePath = targetUrl;
          try {
            if (targetUrl.startsWith('http://') || targetUrl.startsWith('https://')) {
              const urlObj = new URL(targetUrl);
              if (urlObj.origin === window.location.origin) {
                navigatePath = urlObj.pathname + urlObj.search + urlObj.hash;
              }
            }
            setTimeout(() => {
              router.navigateByUrl(navigatePath);
            }, 100);
          } catch (err) {
            console.error('Error parsing navigation URL from PAMP postMessage:', err);
          }
        }
        break;
      }

      default:
        break;
    }
  }

  public closeIframe(fromPopState: boolean = false, depth: number = 1): void {
    if (this.showIframeSubject.value) {
      this.showIframeSubject.next(false);
      this.isPatientAppModeSubject.next(false);
      this.iframeClosedSubject.next();
    }

    if (this.hasPushedHistoryState) {
      this.hasPushedHistoryState = false;
      if (!fromPopState) {
        const requestedSteps = typeof depth === 'number' && depth > 0 ? Math.floor(depth) : 1;
        const steps = Math.min(requestedSteps, this.MAXIMUM_HISTORY_UNWIND_STEPS);
        window.history.go(-steps);
      }
    }
  }
}
